import {
  Canvas,
  Circle,
  FabricImage,
  Group,
  InteractiveFabricObject,
  Line,
  Rect,
  Textbox,
  Triangle,
} from 'fabric'
import { jsPDF } from 'jspdf'
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import {
  BiAlignLeft,
  BiAlignMiddle,
  BiAlignRight,
  BiBold,
  BiCircle,
  BiCrop,
  BiItalic,
  BiMinus,
  BiRectangle,
  BiRedo,
  BiText,
  BiTrash,
  BiUnderline,
  BiUndo,
} from 'react-icons/bi'
import {
  MdFlipToBack,
  MdFlipToFront,
  MdPhotoSizeSelectLarge,
  MdOutlineChangeHistory,
  MdRotateLeft,
  MdSave,
  MdSaveAs,
  MdAssignmentTurnedIn,
  MdDelete,
  MdPreview,
  MdZoomIn,
  MdZoomOut,
  MdOpenWith,
  MdArrowBack,
} from 'react-icons/md'
import { ToastContainer, toast } from 'react-toastify'

import { LogoNew1 } from 'assets/images'
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
} from 'react-accessible-accordion'
import { AiOutlineLine } from 'react-icons/ai'
import axiosClient from 'services/axios-client'
import { BookCoverContainer } from './style'

import AWS from 'aws-sdk'
import classNames from 'classnames'
import { ColorPicker } from 'components/ColorPicker'
import StyledLoader from 'components/Elements/StyledLoader'
import {
  useHistory,
  useParams,
  withRouter,
  useLocation,
} from 'react-router-dom'
import queryString from 'query-string'
import { useBookQuery, useCoverQuery } from 'store/queries'
import { colors } from 'style'
import { enableAligningGuidelines, enableCenteringGuidelines } from 'lib/fabric'
import { Input, Radio } from 'antd'
import BackgroundSelector from './components/BackgroundSelector'
import {
  fonts,
  historyReducer,
  initalObjectPosition,
  loadBackgroundImage,
  loadFonts,
  zoomCanvas,
} from './CoverEditor.helpers'
import { isValidImageBlob, loadImage, remoteImageToFile } from 'lib/image'
import { SyncOutlined } from '@ant-design/icons'
import SaveCoverModal from './components/SaveCoverModal'
import DeleteCoverModal from './components/DeleteCoverModal'
import { useCrop, usePan } from './hooks'

// Override control defaults to make them more visible.
// TODO: Move fabric stuff into a lib file?
InteractiveFabricObject.ownDefaults = {
  ...InteractiveFabricObject.ownDefaults,
  cornerSize: 40,
  cornerStrokeColor: colors.blueDark,
  cornerColor: '#00008216',
  transparentCorners: false,
  cornerDashArray: [2, 2],
  borderColor: 'orange',
  borderDashArray: [3, 1, 3],
  borderScaleFactor: 2,
}

// aws config
AWS.config.update({
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  region: process.env.REACT_APP_AWS_REGION,
})
const S3_BUCKET = process.env.REACT_APP_S3_BUCKET_NAME

const pixelsPerMm = 11.811023622047244 // At 300 DPI

const visibleHeight = 2480
const visibleWidth = 1748

const bleed = 3.18 * pixelsPerMm
const margin = 12.7 * pixelsPerMm

// Total height in pixels (A5 + bleed) = 2517

// A5: 210 x 148 mm
// Bleed: 3.18 mm
const heightWithBleedPx = visibleHeight + bleed * 2
const widthWithBleedPx = visibleWidth + bleed // No bleed on spine side of page.
const halfBleed = bleed / 2

// Prevent scaling images to a size where printing would suffer.
function handleScaleImg(e, canvas, img) {
  if (img.scaleX > 1 || img.scaleY > 1) {
    img.scaleX = Math.min(img.scaleX, 1)
    img.scaleY = Math.min(img.scaleY, 1)

    // Reset position, otherwise the image will jump around if resizing up/leftwards
    img.left = e.transform.original.left
    img.top = e.transform.original.top

    img.setCoords()
    canvas.requestRenderAll()

    toast.error(
      "Increasing this image above it's current size will make it not detailed enough for printing. Please add a higher resolution image to scale it further.",
      { toastId: 'img-warning' },
    )
  }
}

function CoverEditor() {
  const location = useLocation()
  const routerHistory = useHistory()

  const { bookId, coverId } = useParams()
  const { isNew, templateId } = queryString.parse(location.search)

  const { t } = useTranslation()

  const { data: coverData, isFetching: isCoverDataFetching } =
    useCoverQuery(coverId)
  const { data: templateData, isFetching: isTemplateDataFetching } =
    useCoverQuery(templateId)
  const { data: bookDetail } = useBookQuery(bookId)

  const [canvasHistory, dispatchHistory] = useReducer(historyReducer, {})

  const frontCanvasElRef = useRef()
  const backCanvasElRef = useRef()
  const spineCanvasElRef = useRef()

  const frontCanvasRef = useRef()
  const backCanvasRef = useRef()
  const spineCanvasRef = useRef()

  const activeCanvasRef = useRef()
  const canvasWrapperElRef = useRef()
  const ignoreHistory = useRef(false)

  const [strokeColorValue, setStrokeColorValue] = useState('black')
  const [fillColorValue, setFillColorValue] = useState('#fff0')
  const [fontFamilyValue, setfontFamilyValue] = useState('')
  const [remoteImgURL, setRemoteImgURL] = useState('')
  const [coverName, setCoverName] = useState('')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showSaveCoverModal, setShowSaveCoverModal] = useState(false)
  const [currentEditTarget, setCurrentEditTarget] = useState('front')
  const [activeSelection, setActiveSelection] = useState([])
  const [backgroundColor, setBackgroundColor] = useState('#ffffff')
  const [strokeWidth, setStrokeWidth] = useState(1)
  const [addingRemoteImg, setAddingRemoteImg] = useState(false)
  const [addingLocalImg, setAddingLocalImg] = useState(false)

  const [frontBackgroundImage, setFrontBackgroundImage] = useState(null)
  const [spineBackgroundImage, setSpineBackgroundImage] = useState(null)
  const [backBackgroundImage, setBackBackgroundImage] = useState(null)
  const [canvasSpacerConfig, setCanvasSpacerConfig] = useState({
    isPillarBoxed: false,
    width: '100%',
    height: '100%',
  })

  const frontCanvas = frontCanvasRef?.current || null
  const backCanvas = backCanvasRef?.current || null
  const spineCanvas = spineCanvasRef?.current || null

  const canvases = useMemo(
    () => ({
      front: frontCanvas,
      spine: spineCanvas,
      back: backCanvas,
    }),
    [frontCanvas, spineCanvas, backCanvas],
  )

  const spineHistory = canvasHistory.canvases?.spine
  const frontHistory = canvasHistory.canvases?.front
  const backHistory = canvasHistory.canvases?.back

  const currentCanvasHistory = canvasHistory.canvases
    ? canvasHistory.canvases[currentEditTarget]
    : null

  const activeCanvas = canvases[currentEditTarget]
  activeCanvasRef.current = activeCanvas
  const activeObject = activeSelection?.length === 1 ? activeSelection[0] : null

  let currentCanvasBackgroundImg = frontBackgroundImage
  if (currentEditTarget === 'spine') {
    currentCanvasBackgroundImg = spineBackgroundImage
  } else if (currentEditTarget === 'back') {
    currentCanvasBackgroundImg = backBackgroundImage
  }

  const pageCount = bookDetail.pages
  const spineMargin = 3.175 * pixelsPerMm

  // See Lulu Book Creation Guide for formula:
  // https://assets.lulu.com/media/guides/en/lulu-book-creation-guide.pdf
  const spineWidth = pixelsPerMm * (pageCount / 17.48 + 1.524)

  const totalCoverWidth = widthWithBleedPx * 2 + spineWidth

  const isActiveTextObject = activeObject?.type === 'textbox'
  const isActiveImgObject = activeObject?.type === 'image'

  // ------------------------------load json if it exists in session storage or show name modal.

  const canvasWrapperEl = canvasWrapperElRef.current

  const pushHistory = useCallback(
    async (canvas) => {
      if (ignoreHistory.current) return

      dispatchHistory({
        type: 'PUSH',
        activeCanvas: currentEditTarget,
        newState: canvas.toJSON(),
      })
    },
    [currentEditTarget],
  )

  const { isPanning, togglePanMode } = usePan(activeCanvasRef, canvases)
  const { cropImage, enterCropMode, applyCrop } = useCrop(
    activeCanvas,
    activeObject,
    ignoreHistory,
    pushHistory,
  )

  const canUndo = currentCanvasHistory?.index > 0 && !cropImage
  const canRedo =
    currentCanvasHistory?.index < currentCanvasHistory?.stack.length - 1 &&
    !cropImage

  useEffect(loadFonts, [])

  useEffect(() => {
    if (isNew) {
      localStorage.removeItem('cover-history')
      routerHistory.replace({
        pathname: location.pathname,
        search: '',
      })
    }
  }, [isNew, location.pathname, routerHistory])

  useEffect(
    function saveHistoryToLocalStorage() {
      if (frontCanvasRef.current) {
        localStorage.setItem('cover-history', JSON.stringify(canvasHistory))
      }
    },
    [coverData?.thumbnail_url, canvasHistory],
  )

  // We need to place buffers beside the cover in order to have the canvas maintain the correct aspect ratio,
  // whilst scaling to fit the available area. Fabricjs will have geometry errors if the canvas is scaled with css
  // while keeping the same element size. To remove this we would need to to decouple the size of the canvas from the cover size.
  useEffect(
    function initializeSpacers() {
      if (canvasWrapperEl && activeCanvas) {
        const setupCanvasSpacers = () => {
          const canvasWidth = activeCanvas.getWidth()
          const canvasHeight = activeCanvas.getHeight()

          const aspectRatio = canvasWidth / canvasHeight

          const canvasWrapperGeometry = canvasWrapperEl.getBoundingClientRect()
          const wrapperHeight = canvasWrapperGeometry.height
          const wrapperWidth = canvasWrapperGeometry.width
          const wrapperAspectRatio = wrapperWidth / wrapperHeight

          // Do we need to calc

          // Pillarboxing
          if (wrapperAspectRatio > aspectRatio) {
            const scalingFactor = wrapperHeight / canvasHeight
            const scaledWidth = canvasWidth * scalingFactor
            const pillarWidth = (wrapperWidth - scaledWidth) / 2

            setCanvasSpacerConfig({
              isPillarBoxed: true,
              width: pillarWidth,
              height: '100%',
            })
          } else {
            // Letterboxing
            const scalingFactor = wrapperWidth / canvasWidth
            const scaledHeight = canvasHeight * scalingFactor
            const bufferHeight = (wrapperHeight - scaledHeight) / 2

            setCanvasSpacerConfig({
              isPillarBoxed: false,
              width: '100%',
              height: bufferHeight,
            })
          }
        }

        setupCanvasSpacers()
        const observer = new ResizeObserver(setupCanvasSpacers)
        observer.observe(canvasWrapperEl)

        return () => {
          observer.disconnect()
        }
      }
    },
    [activeCanvas, canvasWrapperEl],
  )

  const bgImageForSpine = useCallback(
    async (canvas, backgroundImage) => {
      let marginRectangle = new Rect({
        width: spineWidth - spineMargin,
        height: heightWithBleedPx - spineMargin - bleed * 2,
        top: bleed,
        fill: '',
        stroke: colors.gray,
        strokeWidth: spineMargin,
        draggable: false,
        hasControls: false,
        opacity: 0.6,
      })

      const bleedOptions = {
        stroke: colors.blueDark4,
        strokeWidth: bleed,
        draggable: false,
        hasControls: false,
        opacity: 0.4,
      }

      const topBleed = new Line(
        [0, halfBleed, spineWidth, halfBleed],
        bleedOptions,
      )
      const bottomBleed = new Line(
        [
          0,
          heightWithBleedPx - halfBleed,
          spineWidth,
          heightWithBleedPx - halfBleed,
        ],
        bleedOptions,
      )

      const bgGroup = [marginRectangle, topBleed, bottomBleed]

      if (backgroundImage) {
        bgGroup.unshift(backgroundImage)
      }

      canvas.backgroundColor = 'white'
      canvas.backgroundImage = new Group(bgGroup)

      const logo = await FabricImage.fromURL(LogoNew1)
      logo.set({
        left: spineMargin * 2,
        top: heightWithBleedPx * 0.833 + bleed,
        draggable: false,
        hasControls: false,
      })
      logo.scaleToWidth(spineWidth - spineMargin * 4)

      canvas.overlayImage = logo
    },
    [spineMargin, spineWidth],
  )

  const handleWheel = (e) => {
    const coords = { x: e.clientX, y: e.clientY }

    if (e.deltaY < 0) {
      zoomCanvas(activeCanvas, 1, coords)
    } else {
      zoomCanvas(activeCanvas, -1, coords)
    }
  }

  const marginAndBleedForFrontAndBackCover = useCallback(
    (canvas, bgImage = null) => {
      const marginOptions = {
        height: visibleHeight - margin, // Half of stroke is outside the height
        width: visibleWidth - margin,
        top: bleed,
        left: 0,
        fill: '',
        stroke: colors.gray,
        strokeWidth: margin,
        draggable: false,
        hasControls: false,
        opacity: 0.6,
      }

      const bleedOptions = {
        stroke: colors.blueDark4,
        strokeWidth: bleed,
        draggable: false,
        hasControls: false,
        opacity: 0.4,
      }

      let marginRectangle
      let bleedTop
      let bleedSide
      let bleedBottom

      // Calculations take into acount the fact that lines are measured from their center, not absolutely from the top corner.
      if (canvas === frontCanvasRef.current) {
        bleedTop = new Line(
          [0, halfBleed, widthWithBleedPx, halfBleed],
          bleedOptions,
        )
        bleedSide = new Line(
          [
            visibleWidth + halfBleed,
            bleed,
            visibleWidth + halfBleed,
            visibleHeight + bleed,
          ],
          bleedOptions,
        )
        bleedBottom = new Line(
          [
            0,
            heightWithBleedPx - halfBleed,
            widthWithBleedPx,
            heightWithBleedPx - halfBleed,
          ],
          bleedOptions,
        )
        marginRectangle = new Rect(marginOptions)
      } else {
        bleedTop = new Line(
          [0, halfBleed, widthWithBleedPx, halfBleed],
          bleedOptions,
        )

        bleedSide = new Line(
          [halfBleed, bleed, halfBleed, visibleHeight + bleed],
          bleedOptions,
        )
        bleedBottom = new Line(
          [
            0,
            heightWithBleedPx - halfBleed,
            widthWithBleedPx,
            heightWithBleedPx - halfBleed,
          ],
          bleedOptions,
        )
        marginRectangle = new Rect({
          ...marginOptions,
          left: bleed,
        })
      }

      let groupMembers = [marginRectangle, bleedTop, bleedSide, bleedBottom]
      if (bgImage) {
        groupMembers.unshift(bgImage)
      }

      const group = new Group(groupMembers)
      group.selectable = false
      group.evented = false
      canvas.backgroundColor = 'white'
      canvas.backgroundImage = group
      canvas.renderAll()
    },
    [],
  )

  useEffect(
    function runSetup() {
      const isLoadingExistingCover = coverId && !coverData
      const isLoadingTemplate = templateId && !templateData
      if (
        !frontCanvasElRef.current ||
        frontCanvasRef.current ||
        !pageCount ||
        isLoadingExistingCover ||
        isCoverDataFetching ||
        isLoadingTemplate ||
        isTemplateDataFetching
      ) {
        return
      }

      if (coverData?.is_retired) {
        routerHistory.push(`/dashboard/book/${bookId}/publish/cover-templates`)
      }

      // Initialize canvas event handlers
      // TODO: Make an adapter component so we can handle these in the normal react way.
      const setupCanvasEventHandlers = (canvas) => {
        canvas.on('object:added', (e) => {
          e.target.canvas.setActiveObject(e.target)
          setActiveSelection([e.target])
          setStrokeWidth(e.target.strokeWidth)
          setStrokeColorValue(e.target.stroke)
          setFillColorValue(e.target.fill)
          pushHistory(canvas)
          canvas.requestRenderAll()
        })

        canvas.on('object:modified', () => pushHistory(canvas))
        canvas.on('object:removed', () => pushHistory(canvas))

        const handleSelection = ({ selected }) => {
          setActiveSelection(selected || [])

          const singleObjectSelected = selected?.length === 1
          if (singleObjectSelected) {
            const obj = selected[0]

            setStrokeWidth(obj.strokeWidth)
            setStrokeColorValue(obj.stroke)
            setFillColorValue(obj.fill)
          }
        }

        canvas.on('selection:created', handleSelection)
        canvas.on('selection:updated', handleSelection)
        canvas.on('selection:cleared', handleSelection)

        enableCenteringGuidelines(canvas)
        enableAligningGuidelines(canvas)
      }

      async function initializeCanvases() {
        const frontCanvasFabric = new Canvas(frontCanvasElRef.current, {
          width: widthWithBleedPx,
          height: heightWithBleedPx,
        })
        frontCanvasRef.current = frontCanvasFabric

        const spineCanvasFabric = new Canvas(spineCanvasElRef.current, {
          width: spineWidth,
          height: heightWithBleedPx,
        })
        spineCanvasRef.current = spineCanvasFabric

        const backCanvasFabric = new Canvas(backCanvasElRef.current, {
          width: widthWithBleedPx,
          height: heightWithBleedPx,
        })
        backCanvasRef.current = backCanvasFabric

        const lsCover = JSON.parse(localStorage.getItem('cover-history'))

        let shouldReset = false
        if (coverData) {
          setCoverName(coverData.template_name)

          if (lsCover?.key === coverData.thumbnail_url) {
            dispatchHistory({
              type: 'RESET',
              initialState: lsCover,
            })
            await Promise.all([
              frontCanvasFabric.loadFromJSON(
                lsCover.canvases.front.stack[
                  lsCover.canvases.front.stack.length - 1
                ],
              ),
              spineCanvasFabric.loadFromJSON(
                lsCover.canvases.spine.stack[
                  lsCover.canvases.spine.stack.length - 1
                ],
              ),
              backCanvasFabric.loadFromJSON(
                lsCover.canvases.back.stack[
                  lsCover.canvases.back.stack.length - 1
                ],
              ),
            ])
          } else {
            // Different last save, so load from remote
            await Promise.all([
              frontCanvasFabric.loadFromJSON(coverData.frontcover),
              spineCanvasFabric.loadFromJSON(coverData.spine),
              backCanvasFabric.loadFromJSON(coverData.backcover),
            ])

            shouldReset = true
          }
        } else if (templateData) {
          await Promise.all([
            frontCanvasFabric.loadFromJSON(templateData.frontcover),
            spineCanvasFabric.loadFromJSON(templateData.spine),
            backCanvasFabric.loadFromJSON(templateData.backcover),
          ])

          shouldReset = true

          routerHistory.replace({
            pathname: location.pathname,
            search: '',
          })
        } else {
          if (lsCover && lsCover.key === '_UNSAVED') {
            dispatchHistory({
              type: 'RESET',
              initialState: lsCover,
            })
            await Promise.all([
              frontCanvasFabric.loadFromJSON(
                lsCover.canvases.front.stack[
                  lsCover.canvases.front.stack.length - 1
                ],
              ),
              spineCanvasFabric.loadFromJSON(
                lsCover.canvases.spine.stack[
                  lsCover.canvases.spine.stack.length - 1
                ],
              ),
              backCanvasFabric.loadFromJSON(
                lsCover.canvases.back.stack[
                  lsCover.canvases.back.stack.length - 1
                ],
              ),
            ])
          } else {
            marginAndBleedForFrontAndBackCover(frontCanvasRef.current)
            await bgImageForSpine(spineCanvasFabric)
            marginAndBleedForFrontAndBackCover(backCanvasFabric)

            shouldReset = true
          }
        }

        // We have to render before the rest of the setup,
        // or the spine and back covers render don't render on initial view.
        // TODO: Figure this out
        frontCanvasFabric.requestRenderAll()
        spineCanvasFabric.requestRenderAll()
        backCanvasFabric.requestRenderAll()

        if (shouldReset) {
          const initialFront = frontCanvasFabric.toJSON()
          const initialSpine = spineCanvasFabric.toJSON()
          const initialBack = backCanvasFabric.toJSON()

          dispatchHistory({
            type: 'RESET',
            initialState: {
              key: coverData?.thumbnail_url || '_UNSAVED',
              canvases: {
                front: { stack: [initialFront], index: 0 },
                spine: { stack: [initialSpine], index: 0 },
                back: { stack: [initialBack], index: 0 },
              },
            },
          })
        }

        setupCanvasEventHandlers(frontCanvasFabric, 'front')
        setupCanvasEventHandlers(spineCanvasFabric, 'spine')
        setupCanvasEventHandlers(backCanvasFabric, 'back')
      }

      initializeCanvases()
    },
    [
      activeCanvas,
      bgImageForSpine,
      coverId,
      coverData,
      marginAndBleedForFrontAndBackCover,
      pageCount,
      pushHistory,
      spineMargin,
      spineWidth,
      isCoverDataFetching,
      routerHistory,
      bookId,
      templateId,
      templateData,
      isTemplateDataFetching,
      location.pathname,
    ],
  )

  const onAddCircle = () => {
    const options = {
      radius: 72,
      fill: 'transparent',
      stroke: 'black',
      strokeUniform: true,
      strokeWidth: 3,
      noScaleCache: false,
    }
    const position = initalObjectPosition(
      currentEditTarget,
      margin,
      bleed,
      spineMargin,
    )

    const circle = new Circle({ ...options, ...position })

    activeCanvas.add(circle)
  }

  const onAddRectangle = () => {
    const options = {
      width: 200,
      height: 100,
      fill: 'transparent',
      stroke: 'black',
      strokeUniform: true,
      strokeWidth: 3,
      noScaleCache: false,
    }
    const position = initalObjectPosition(
      currentEditTarget,
      margin,
      bleed,
      spineMargin,
    )
    let rectangle = new Rect({ ...options, ...position })

    activeCanvas.add(rectangle)
  }

  const onAddLine = () => {
    const options = {
      stroke: 'black',
      strokeUniform: true,
      strokeWidth: 3,
      noScaleCache: false,
    }

    const position = initalObjectPosition(
      currentEditTarget,
      margin,
      bleed,
      spineMargin,
    )

    let line = new Line([50, 10, 200, 150], { ...options, ...position })
    activeCanvas.add(line)
  }

  const onTriangle = () => {
    const options = {
      width: 150,
      height: 150,
      fill: 'transparent',
      stroke: 'black',
      strokeUniform: true,
      strokeWidth: 3,
      cornerColor: 'blue',
      noScaleCache: false,
    }
    const position = initalObjectPosition(
      currentEditTarget,
      margin,
      bleed,
      spineMargin,
    )

    const triangle = new Triangle({ ...options, ...position })
    activeCanvas.add(triangle)
  }

  const onAddText = () => {
    const text = 'YourTextHere'

    const options = {
      fontSize: 96,
      fontFamily: fontFamilyValue,
      stroke: 'black',
      fill: 'black',
      textAlign: 'center',
    }

    const position = initalObjectPosition(
      currentEditTarget,
      margin,
      bleed,
      spineMargin,
    )

    const tb = new Textbox(text, { ...options, ...position })

    // Prevent text from being stretched vertically.
    tb.setControlsVisibility({
      mt: false,
      mb: false,
    })

    activeCanvas.add(tb)
  }

  const onAddTextVerticallyTopToBottom = () => {
    const text = 'YourTextHere'
    const tb = new Textbox(text, {
      fontFamily: fontFamilyValue,
      fontSize: 96,
      stroke: 'black',
      fill: 'black',
      angle: 90,
      originX: 'left',
      originY: 'bottom',
      textAlign: 'center',
    })

    tb.setControlsVisibility({
      ml: false,
      mr: false,
    })

    spineCanvas.add(tb)
    spineCanvas.centerObject(tb)
  }

  const onAddTextVerticallyBottomToTop = () => {
    const text = 'YourTextHere'
    const tb = new Textbox(text, {
      fontFamily: fontFamilyValue,
      fontSize: 96,
      stroke: 'black',
      fill: 'black',
      angle: 270,
      originX: 'right',
      originY: 'top',
      textAlign: 'center',
    })

    tb.setControlsVisibility({
      ml: false,
      mr: false,
    })

    spineCanvas.add(tb)
    spineCanvas.centerObject(tb)
  }

  const uploadImageToBucket = async (img) => {
    const fileExtension = img.name.includes('.')
      ? img.name.split('.').pop()
      : 'png' // get the file extension

    const uniqueId = Math.random().toString(16).slice(2, 10)
    // Prevent clashes.
    const renamedImg = new File([img], uniqueId + '-' + img.name, {
      type: img.type,
    })
    const bookId = bookDetail?.id

    const uploadRes = await axiosClient.post(
      `/PDF/book/${bookId}/cover/upload`,
      { fileExtension, fileName: renamedImg.name, kind: 'cover' },
    )
    if (!uploadRes.ok) {
      throw new Error('Failed to create upload link')
    }

    const { url } = uploadRes.data

    await fetch(url, { method: 'PUT', body: renamedImg })

    const downloadRes = await axiosClient.post(
      `/PDF/book/${bookId}/cover/download`,
      { fileName: renamedImg.name, kind: 'cover' },
    )
    if (!downloadRes.ok) {
      throw new Error('Failed to create download link')
    }

    return { url: downloadRes.data, fileName: renamedImg.name }
  }

  const uploadThumbnailToBucket = async (img) => {
    const renamedImg = new File(
      [img],
      coverName + '-' + new Date().toISOString(),
      {
        type: img.type,
      },
    )
    const bookId = bookDetail?.id

    const uploadRes = await axiosClient.post(
      `/PDF/book/${bookId}/cover/thumbnail/upload`,
      { fileName: renamedImg.name },
    )
    if (!uploadRes.ok) {
      throw new Error('Failed to create upload link')
    }

    const { url, s3FileName } = uploadRes.data

    await fetch(url, { method: 'PUT', body: renamedImg })

    const downloadRes = await axiosClient.post(
      `/PDF/book/${bookId}/cover/thumbnail/download`,
      { fileName: s3FileName },
    )
    if (!downloadRes.ok) {
      throw new Error('Failed to create download link')
    }

    return { url: downloadRes.data, fileName: s3FileName }
  }

  const uploadPdfToBucket = async (file) => {
    const bookId = bookDetail?.id

    const uploadRes = await axiosClient.post(
      `/PDF/book/${bookId}/cover/finalize`,
      { fileExtension: 'pdf' },
    )
    if (!uploadRes.ok) {
      throw new Error('Failed to create upload link')
    }

    const { url } = uploadRes.data

    await fetch(url, { method: 'PUT', body: file }).catch((e) => console.log(e))
  }

  const addRemoteImgByUrl = async (e, remoteUrl) => {
    e.preventDefault()
    setAddingRemoteImg(true)

    try {
      const file = await remoteImageToFile(remoteUrl)
      const { url } = await uploadImageToBucket(file)

      const img = await FabricImage.fromURL(URL.createObjectURL(file))
      activeCanvas.add(img)

      img.setSrc(url, {
        crossOrigin: 'anonymous',
      })
      img.on('modified', (e) => handleScaleImg(e, activeCanvas, img))
      activeCanvas.renderAll()
    } catch (error) {
      toast.error(error.message || 'Failed to add remote image')
    } finally {
      setAddingRemoteImg(false)
      setRemoteImgURL('')
    }
  }

  const onUploadImage = async (e) => {
    if (e.target.files.length) {
      setAddingLocalImg(true)
      try {
        const image = e.target.files[0]

        if (!(await isValidImageBlob(image))) {
          throw new Error('The selected file is not a valid image.')
        }

        const { url } = await uploadImageToBucket(image)

        const img = await FabricImage.fromURL(URL.createObjectURL(image))
        activeCanvas.add(img)

        img.setSrc(url, {
          crossOrigin: 'anonymous',
        })
        img.on('modified', (e) => handleScaleImg(e, activeCanvas, img))
        activeCanvas.renderAll()
      } catch (error) {
        toast.error(error.message || 'Failed to upload image')
      } finally {
        setAddingLocalImg(false)
      }
    }
  }

  const selectStrokeColor = (value) => {
    setStrokeColorValue(value)
    activeObject.set({
      stroke: value,
    })
    activeCanvas.renderAll()
  }

  const selectFillColor = (value) => {
    setFillColorValue(value)
    activeObject.set({
      fill: value,
    })
    activeCanvas.renderAll()
  }

  const handleStrokeWidthChange = (e) => {
    const parsed = parseInt(e.target.value)
    const width = parsed || 1

    setStrokeWidth(width)
    activeObject.set({
      strokeWidth: width,
    })
    activeCanvas.renderAll()
    pushHistory(activeCanvas)
  }

  const textFontFamily = (value) => {
    setfontFamilyValue(value.target.value)

    activeObject.set({
      fontFamily: value.target.value,
    })
    activeCanvas.renderAll()
    pushHistory(activeCanvas)
  }

  const textBold = () => {
    if (activeObject.fontWeight !== 'bold') {
      activeObject.set({
        fontWeight: 'bold',
      })
    } else {
      activeObject.set({
        fontWeight: 'normal',
      })
    }
    activeCanvas.renderAll()
    pushHistory(activeCanvas)
  }

  const textUnderline = () => {
    activeObject.set({
      underline: !activeObject.underline,
    })
    activeCanvas.renderAll()
    pushHistory(activeCanvas)
  }

  const textStrikeThrough = () => {
    activeObject.set({
      overline: !activeObject.overline,
    })
    activeCanvas.renderAll()
    pushHistory(activeCanvas)
  }

  const textItalic = () => {
    if (activeObject.fontStyle !== 'italic') {
      activeObject.set({
        fontStyle: 'italic',
      })
    } else if (activeObject.fontStyle === 'italic') {
      activeObject.set({
        fontStyle: 'normal',
      })
    }
    activeCanvas.renderAll()
    pushHistory(activeCanvas)
  }

  // Justify text left
  const textLeft = () => {
    activeObject.set({
      textAlign: 'left',
    })
    activeCanvas.renderAll()
    pushHistory(activeCanvas)
  }

  // Justify text center
  const textCenter = () => {
    activeObject.set({
      textAlign: 'center',
    })
    activeCanvas.renderAll()
    pushHistory(activeCanvas)
  }

  // Justify text right
  const textRight = () => {
    activeObject.set({
      textAlign: 'right',
    })
    activeCanvas.renderAll()
    pushHistory(activeCanvas)
  }

  const sendForward = () => {
    // bringObjectForward seems buggy, so we use bringObjectToFront instead.
    // TODO: Further investigate bringObjectForward and if it's a fabric bug.
    activeSelection.forEach((obj) => activeCanvas.bringObjectToFront(obj))

    activeCanvas.discardActiveObject()
    activeCanvas.renderAll()

    setActiveSelection([])
    pushHistory(activeCanvas)
  }

  const sendBackward = () => {
    activeSelection.forEach((obj) => activeCanvas.sendObjectBackwards(obj))
    activeCanvas.discardActiveObject()
    activeCanvas.renderAll()

    setActiveSelection([])
    pushHistory(activeCanvas)
  }

  const resetRotation = () => {
    activeObject.set({
      angle: 0,
    })
    activeObject.setCoords()
    activeCanvas.renderAll()
    pushHistory(activeCanvas)
  }

  const deleteObj = () => {
    activeCanvas.discardActiveObject()
    activeSelection.forEach((obj) => activeCanvas.remove(obj))
    activeCanvas.requestRenderAll()

    setActiveSelection([])
    pushHistory(activeCanvas)
  }

  function undo() {
    if (ignoreHistory.current) return

    const currHistory = canvasHistory.canvases[currentEditTarget]
    const newIndex = currHistory.index - 1
    ignoreHistory.current = true

    activeCanvas
      .loadFromJSON(currHistory.stack[newIndex > 0 ? newIndex : 0])
      .then(() => {
        activeCanvas.renderAll()
        ignoreHistory.current = false
      })

    dispatchHistory({ type: 'UNDO', activeCanvas: currentEditTarget })
  }

  function redo() {
    if (ignoreHistory.current) return

    const currHistory = canvasHistory.canvases[currentEditTarget]

    const newIndex = currHistory.index + 1
    ignoreHistory.current = true
    const newIndexOutOfBounds = newIndex >= currHistory.stack.length

    activeCanvas
      .loadFromJSON(
        currHistory.stack[
          newIndexOutOfBounds ? currHistory.stack.length - 1 : newIndex
        ],
      )
      .then(() => {
        activeCanvas.renderAll()
        ignoreHistory.current = false
      })

    dispatchHistory({ type: 'REDO', activeCanvas: currentEditTarget })
  }

  const deleteCover = () => {
    if (coverId) {
      const deleteData = async () => {
        const templateResult = await axiosClient.get(
          `/templates/delete/${coverId}`,
        )

        if (templateResult.ok) {
          toast.success('Your Template is deleted!')

          routerHistory.replace(
            `/dashboard/book/${bookDetail.id}/publish/cover-templates`,
          )
        } else {
          toast.error('Failed to delete template')

          return
        }
      }
      deleteData()

      setCoverName('')
    }

    localStorage.removeItem('cover-history')

    Object.values(canvases).forEach((canvas) => {
      canvas.clear()
      canvas.setViewportTransform([1, 0, 0, 1, 0, 0])
    })
    marginAndBleedForFrontAndBackCover(frontCanvas)
    bgImageForSpine(spineCanvas)
    marginAndBleedForFrontAndBackCover(backCanvas)
    activeCanvas.requestRenderAll()

    setShowDeleteModal(false)
  }

  const updateCover = async () => {
    const thumb = await generateThumbnail()
    const { url } = await uploadThumbnailToBucket(thumb)

    const payload = {
      bookId: bookDetail.id,
      spine: JSON.stringify(spineHistory.stack[spineHistory.stack.length - 1]),
      front: JSON.stringify(frontHistory.stack[frontHistory.stack.length - 1]),
      back: JSON.stringify(backHistory.stack[backHistory.stack.length - 1]),
      thumbnailUrl: url,
    }

    const templateResult = await axiosClient.post(`/covers/${coverId}`, payload)

    if (templateResult.ok) {
      dispatchHistory({
        type: 'SAVE',
        key: url,
      })
      toast.success('Your cover is updated!')
    } else {
      toast.success('Something ... went wrong!')
    }
  }

  const saveCover = async (name) => {
    const thumb = await generateThumbnail()
    const { url } = await uploadThumbnailToBucket(thumb)

    try {
      const payloadForPost = {
        name,
        spine: JSON.stringify(
          spineHistory.stack[spineHistory.stack.length - 1],
        ),
        front: JSON.stringify(
          frontHistory.stack[frontHistory.stack.length - 1],
        ),
        back: JSON.stringify(backHistory.stack[backHistory.stack.length - 1]),
        bookId: bookDetail?.id,
        thumbnailUrl: url,
      }

      const res = await axiosClient.post(`/templates/create/`, payloadForPost)

      toast.success('Cover saved.')
      setShowSaveCoverModal(false)
      setCoverName(name)

      dispatchHistory({
        type: 'SAVE',
        key: url,
      })

      // TODO: Deal with this in the backend
      routerHistory.replace(
        `/dashboard/book/${bookDetail.id}/publish/cover-design/${res.data.templates.generatedFields[0].longValue}`,
      )
    } catch (error) {
      toast.error('Something went wrong!')

      throw error
    }
  }

  // Renders the canvases to data URL images.
  const renderCanvases = async () => {
    const [clonedFrontCanvas, clonedSpineCanvas, clonedBackCanvas] =
      await Promise.all([
        frontCanvas.clone(),
        spineCanvas.clone(),
        backCanvas.clone(),
      ])

    clonedFrontCanvas.backgroundImage = null
    if (frontBackgroundImage) {
      clonedFrontCanvas.backgroundImage = await loadBackgroundImage(
        clonedFrontCanvas,
        frontBackgroundImage,
      )
    }
    clonedSpineCanvas.backgroundImage = null
    if (spineBackgroundImage) {
      clonedSpineCanvas.backgroundImage = await loadBackgroundImage(
        clonedSpineCanvas,
        spineBackgroundImage,
      )
    }
    clonedBackCanvas.backgroundImage = null
    if (backBackgroundImage) {
      clonedBackCanvas.backgroundImage = await loadBackgroundImage(
        clonedBackCanvas,
        backBackgroundImage,
      )
    }

    const frontCanvasWithBase64 = clonedFrontCanvas.toJSON(),
      spineCanvasWithBase64 = clonedSpineCanvas.toJSON(),
      backCanvasWithBase64 = clonedBackCanvas.toJSON()

    async function loadCanvasFromJSON(jsonFile, canvasId, canvasRef) {
      // new canvas with base64 images
      const canvas = new Canvas(canvasId, {
        width: canvasRef.width,
        height: canvasRef.height,
      })

      return await canvas.loadFromJSON(jsonFile)
    }

    async function loadCanvases() {
      const front = await loadCanvasFromJSON(
        frontCanvasWithBase64,
        'frontCanvas',
        frontCanvas,
      )
      const spine = await loadCanvasFromJSON(
        spineCanvasWithBase64,
        'spineCanvas',
        spineCanvas,
      )
      const back = await loadCanvasFromJSON(
        backCanvasWithBase64,
        'backCanvas',
        backCanvas,
      )

      return { front, spine, back }
    }

    const { front, spine, back } = await loadCanvases()

    return [back.toDataURL(), spine.toDataURL(), front.toDataURL()]
  }

  async function generateThumbnail() {
    const thumbnailHeight = 300

    const orderedImageUrls = await renderCanvases()
    const images = await Promise.all(orderedImageUrls.map(loadImage))

    const totalWidth = images.reduce((sum, img) => sum + img.width, 0)

    const tempCanvas = document.createElement('canvas')
    tempCanvas.width = totalWidth
    tempCanvas.height = visibleHeight
    const tempCtx = tempCanvas.getContext('2d')

    let x = 0
    for (const img of images) {
      tempCtx.drawImage(img, x, 0)
      x += img.width
    }

    const aspect = totalWidth / visibleHeight
    const finalWidth = Math.round(thumbnailHeight * aspect)

    const finalCanvas = document.createElement('canvas')
    finalCanvas.width = finalWidth
    finalCanvas.height = thumbnailHeight
    const finalCtx = finalCanvas.getContext('2d')

    finalCtx.drawImage(tempCanvas, 0, 0, finalWidth, thumbnailHeight)

    return await new Promise((resolve, reject) => {
      finalCanvas.toBlob((blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Failed to generate thumbnail'))
        }
      })
    })
  }

  const createPdf = async () => {
    const [backImage, spineImage, frontImage] = await renderCanvases()
    const pdf = new jsPDF({
      orientation: 'l',
      unit: 'px',
      format: [totalCoverWidth, heightWithBleedPx],
      hotfixes: ['px_scaling'],
    })

    pdf.addImage(backImage, 'PNG', 0, 0, widthWithBleedPx, heightWithBleedPx)
    pdf.addImage(
      spineImage,
      'PNG',
      widthWithBleedPx,
      0,
      spineWidth,
      heightWithBleedPx,
    )
    pdf.addImage(
      frontImage,
      'PNG',
      widthWithBleedPx + spineWidth,
      0,
      widthWithBleedPx,
      heightWithBleedPx,
    )

    return pdf
  }

  const previewCover = async () => {
    const pdf = await createPdf()
    window.open(pdf.output('bloburl'), '_blank')
  }

  const approveCover = async () => {
    const pdf = await createPdf()
    const pdfBlob = pdf.output('blob')
    const url = window.URL.createObjectURL(pdfBlob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'mybook.pdf'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
    toast.success('Your cover is approved for printing!')

    const file = new File([pdfBlob], 'pdf', { type: 'pdf' })

    const pdfURL = await uploadPdfToBucket(file)
    console.log(pdfURL)

    await axiosClient.put(`/book/${bookDetail.id}`, {
      cover_confirmed: true,
    })

    toast.success('Your cover is approved for printing!')
  }

  const classHiddenIfSpineInactive = {
    hidden: currentEditTarget !== 'spine',
  }
  const classHiddenIfSpineActive = {
    hidden: currentEditTarget === 'spine',
  }

  if (!bookDetail.id) return <StyledLoader />

  return (
    <>
      <BookCoverContainer>
        <SaveCoverModal
          open={showSaveCoverModal}
          onSave={saveCover}
          onClose={() => setShowSaveCoverModal(false)}
          defaultCoverName={coverName}
        />

        <DeleteCoverModal
          show={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onDelete={deleteCover}
        />

        <div className="page-view-area">
          <div className="tools-section">
            <Accordion allowMultipleExpanded={true} allowZeroExpanded={true}>
              <AccordionItem>
                <AccordionItemHeading>
                  <AccordionItemButton>{t('elements')}</AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel>
                  <div>
                    <BiCircle
                      onClick={onAddCircle}
                      size={32}
                      className="mr-2 option-icon icon-style-2 cursor"
                      title={t('circle')}
                    />
                    <BiRectangle
                      onClick={onAddRectangle}
                      size={32}
                      className="mr-2 option-icon icon-style-2 cursor"
                      title={t('rectangle')}
                    />
                    <BiMinus
                      onClick={onAddLine}
                      size={32}
                      className="mr-2 option-icon icon-style-2 cursor"
                      title={t('line')}
                    />
                    <MdOutlineChangeHistory
                      onClick={onTriangle}
                      size={32}
                      className="mr-2 option-icon icon-style-2 cursor"
                      title={t('triangle')}
                    />
                  </div>
                </AccordionItemPanel>
              </AccordionItem>
              <AccordionItem>
                <AccordionItemHeading>
                  <AccordionItemButton>{t('text')}</AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel>
                  <p
                    className={classNames('cursor', classHiddenIfSpineActive)}
                    onClick={onAddText}
                  >
                    <BiText
                      size={24}
                      className={classNames(
                        'mr-2 option-icon icon-style-1',
                        classHiddenIfSpineActive,
                      )}
                    />{' '}
                    {t('text')}
                  </p>

                  <p
                    className={classNames('cursor', classHiddenIfSpineInactive)}
                    onClick={onAddTextVerticallyTopToBottom}
                  >
                    <BiText
                      size={24}
                      className={classNames(
                        'mr-2 option-icon icon-style-1',
                        classHiddenIfSpineInactive,
                      )}
                    />{' '}
                    {t('top_to_bottom')}
                  </p>
                  <p
                    className={classNames('cursor', classHiddenIfSpineInactive)}
                    onClick={onAddTextVerticallyBottomToTop}
                  >
                    <BiText
                      size={24}
                      className={classNames(
                        'mr-2 mt-2 option-icon icon-style-1',
                        classHiddenIfSpineInactive,
                      )}
                    />{' '}
                    {t('bottom_to_top')}
                  </p>
                </AccordionItemPanel>
              </AccordionItem>
              <AccordionItem>
                <AccordionItemHeading>
                  <AccordionItemButton>{t('images')}</AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel>
                  <div className="add-img-panel">
                    <div>
                      <form
                        onSubmit={(e) => addRemoteImgByUrl(e, remoteImgURL)}
                        className="mb-4"
                      >
                        <label>{t('load_image_by_url')} : &nbsp;</label>
                        <Input
                          type="text"
                          value={remoteImgURL}
                          placeholder={t('enter_image_url_here')}
                          onChange={(e) => setRemoteImgURL(e.target.value)}
                          className="mb-3 img-url-input"
                        />
                        <div className="d-flex">
                          <button
                            className="btn-blue btn-dash btn btn-primary"
                            type="submit"
                          >
                            {t('add_image')}
                          </button>
                          {addingRemoteImg && (
                            <div className="spinner-wrapper ml-2">
                              <SyncOutlined spin />
                            </div>
                          )}
                        </div>
                      </form>
                    </div>
                    <label>{t('select_image')} :</label>{' '}
                    <div className="d-flex upload-wrapper">
                      <input
                        className="img-upload"
                        type="file"
                        onChange={onUploadImage}
                      />
                      {addingLocalImg && (
                        <div className="spinner-wrapper ml-2">
                          <SyncOutlined spin />
                        </div>
                      )}
                    </div>
                  </div>
                </AccordionItemPanel>
              </AccordionItem>
            </Accordion>
            <div
              className="danger"
              style={{
                marginBottom: '15px',
                marginTop: '30px',
                padding: '4px 12px',
              }}
            >
              <p>
                <strong>{t('note')} : </strong> {t('note_text')}
              </p>
            </div>
          </div>

          <div
            id="mmain"
            className={classNames('canvas-area', {
              'flex-row': canvasSpacerConfig.isPillarBoxed,
            })}
            ref={canvasWrapperElRef}
          >
            {activeObject && (
              <div className="floating-tools ft-left">
                {isActiveTextObject && (
                  <>
                    <BiBold
                      title={t('bold')}
                      onClick={textBold}
                      size={24}
                      className="mt-2 cursor"
                    />
                    <BiItalic
                      title={t('italic')}
                      onClick={textItalic}
                      size={24}
                      className="mt-2 cursor"
                    />
                    <BiUnderline
                      title={t('underline')}
                      onClick={textUnderline}
                      size={24}
                      className="mt-2 cursor"
                    />
                    <AiOutlineLine
                      onClick={textStrikeThrough}
                      size={24}
                      className="mt-2 cursor"
                      title={t('overline')}
                    />
                    <BiAlignLeft
                      onClick={textLeft}
                      size={24}
                      className="mt-2 cursor"
                      title={t('left')}
                    />
                    <BiAlignMiddle
                      onClick={textCenter}
                      size={24}
                      className="mt-2 cursor"
                      title={t('center')}
                    />
                    <BiAlignRight
                      onClick={textRight}
                      size={24}
                      className="mt-2 cursor"
                      title={t('right')}
                    />
                  </>
                )}
                {isActiveImgObject && (
                  <>
                    <MdPhotoSizeSelectLarge
                      onClick={enterCropMode}
                      size={24}
                      className="mt-2 cursor"
                      title={t('select_image_to_crop')}
                    />
                  </>
                )}
                {cropImage && (
                  <BiCrop
                    onClick={applyCrop}
                    size={24}
                    className="mt-2 cursor"
                    title={t('crop')}
                  />
                )}
                {activeSelection.length ? (
                  <>
                    {activeObject?.angle !== 0 && (
                      <MdRotateLeft
                        onClick={resetRotation}
                        size={24}
                        className="mt-2 cursor"
                        title={t('reset_rotation')}
                      />
                    )}
                    <MdFlipToFront
                      onClick={sendForward}
                      size={24}
                      className="mt-2 cursor"
                      title={t('bring_forward')}
                    />
                    <MdFlipToBack
                      onClick={sendBackward}
                      size={24}
                      className="mt-2 cursor"
                      title={t('send_back')}
                    />
                    <BiTrash
                      onClick={deleteObj}
                      size={24}
                      className="mt-2 cursor"
                      title={t('delete_selected')}
                    />
                  </>
                ) : (
                  ''
                )}
              </div>
            )}
            <div
              className="canvas-buffer"
              style={{
                minWidth: canvasSpacerConfig.width,
                minHeight: canvasSpacerConfig.height,
              }}
            />
            <div
              className={classNames('canvas-wrapper', {
                hide: currentEditTarget !== 'front',
              })}
              onWheel={handleWheel}
            >
              <canvas ref={frontCanvasElRef} id="canvas-front" />
            </div>
            <div
              className={classNames('canvas-wrapper', {
                hide: currentEditTarget !== 'spine',
              })}
              onWheel={handleWheel}
            >
              <canvas ref={spineCanvasElRef} id="canvas-spine" />
            </div>
            <div
              className={classNames('canvas-wrapper', {
                hide: currentEditTarget !== 'back',
              })}
              onWheel={handleWheel}
            >
              <canvas ref={backCanvasElRef} id="canvas-back" />
            </div>
            <div
              className="canvas-buffer"
              style={{
                minWidth: canvasSpacerConfig.width,
                minHeight: canvasSpacerConfig.height,
              }}
            />
            <div className="floating-tools ft-right">
              <MdZoomIn
                size={24}
                title="Zoom in"
                className="cursor mb-1"
                onClick={() => zoomCanvas(activeCanvas, 1)}
              />
              <MdZoomOut
                size={24}
                title="Zoom out"
                className="cursor"
                onClick={() => zoomCanvas(activeCanvas, -1)}
              />
              <MdOpenWith
                size={24}
                title={isPanning ? 'Exit pan mode' : 'Enter pan mode'}
                className={classNames('cursor', { 'active-pan': isPanning })}
                onClick={togglePanMode}
              />
            </div>
          </div>

          <div className="tools-section">
            <Radio.Group
              defaultValue="front"
              buttonStyle="solid"
              onChange={(e) => {
                activeCanvas.discardActiveObject()
                activeCanvas.requestRenderAll()
                setCurrentEditTarget(e.target.value)
                setActiveSelection([])
                setStrokeColorValue('black')
                setFillColorValue('#fff0')
              }}
              className="cover-section-tabs"
            >
              <Radio.Button className="cover-section-tab" value="front">
                {t('front_cover')}
              </Radio.Button>
              <Radio.Button className="cover-section-tab" value="spine">
                {t('spine')}
              </Radio.Button>
              <Radio.Button className="cover-section-tab" value="back">
                {t('back_cover')}
              </Radio.Button>
            </Radio.Group>
            <div className="file-controls">
              <div className="file-nav">
                <MdPreview
                  size={24}
                  className="mr-2 cursor"
                  title={t('preview')}
                  onClick={previewCover}
                />
                <MdSave
                  size={24}
                  className="mr-2 cursor"
                  title={t('save')}
                  onClick={() => {
                    if (coverId) {
                      updateCover()
                    } else {
                      setShowSaveCoverModal(true)
                    }
                  }}
                />
                {coverId && (
                  <MdSaveAs
                    size={24}
                    className="mr-2 cursor"
                    title={t('save_as')}
                    onClick={() => setShowSaveCoverModal(true)}
                  />
                )}
                <MdAssignmentTurnedIn
                  size={24}
                  className="mr-2 cursor"
                  title="Approve for Print"
                  onClick={approveCover}
                />
                <MdDelete
                  size={24}
                  className="mr-2 cursor"
                  title="Delete"
                  onClick={() => setShowDeleteModal(true)}
                />
                <MdArrowBack
                  size={24}
                  className="mr-2 cursor"
                  title="Back to all covers"
                  onClick={() =>
                    routerHistory.push(
                      `/dashboard/book/${bookId}/publish/cover-templates`,
                    )
                  }
                />
              </div>
            </div>
            <div className="mt-2 d-flex justify-content-center">
              <BiUndo
                title={t('undo')}
                onClick={undo}
                size={24}
                className={classNames('mt-2', {
                  cursor: canUndo,
                  disabled: !canUndo,
                })}
              />
              <BiRedo
                title={t('redo')}
                onClick={redo}
                size={24}
                className={classNames('mt-2', {
                  cursor: canRedo,
                  disabled: !canRedo,
                })}
              />
            </div>
            <div className="background-controls mt-2">
              <label>
                Background Color:
                <ColorPicker
                  className="color-input"
                  type="color"
                  color={backgroundColor}
                  onClose={() => pushHistory(activeCanvas)}
                  onChange={(val) => {
                    if (currentEditTarget === 'front') {
                      setFrontBackgroundImage(null)
                      marginAndBleedForFrontAndBackCover(activeCanvas)
                    } else if (currentEditTarget === 'spine') {
                      setSpineBackgroundImage(null)
                      bgImageForSpine(activeCanvas)
                    } else if (currentEditTarget === 'back') {
                      setBackBackgroundImage(null)
                      marginAndBleedForFrontAndBackCover(activeCanvas)
                    }
                    activeCanvas.backgroundColor = val
                    setBackgroundColor(val)
                    activeCanvas.requestRenderAll()
                  }}
                />
              </label>

              <BackgroundSelector
                background={currentCanvasBackgroundImg}
                onSelectBackground={async (pattern) => {
                  const img = await loadBackgroundImage(activeCanvas, pattern)

                  if (currentEditTarget === 'front') {
                    setFrontBackgroundImage(pattern)
                    marginAndBleedForFrontAndBackCover(activeCanvas, img)
                  } else if (currentEditTarget === 'spine') {
                    setSpineBackgroundImage(pattern)
                    bgImageForSpine(activeCanvas, img)
                  } else if (currentEditTarget === 'back') {
                    setBackBackgroundImage(pattern)
                    marginAndBleedForFrontAndBackCover(activeCanvas, img)
                  }
                  setBackgroundColor('#ffffff')
                  activeCanvas.requestRenderAll()
                  dispatchHistory({
                    type: 'PUSH',
                    activeCanvas: currentEditTarget,
                    newState: activeCanvas.toJSON(),
                  })
                }}
                onRemoveBackground={() => {
                  if (currentEditTarget === 'front') {
                    setFrontBackgroundImage(null)
                    marginAndBleedForFrontAndBackCover(activeCanvas, null)
                  } else if (currentEditTarget === 'spine') {
                    setSpineBackgroundImage(null)
                    bgImageForSpine(activeCanvas, null)
                  } else if (currentEditTarget === 'back') {
                    setBackBackgroundImage(null)
                    marginAndBleedForFrontAndBackCover(activeCanvas, null)
                  }
                  activeCanvas.requestRenderAll()
                  dispatchHistory({
                    type: 'PUSH',
                    activeCanvas: currentEditTarget,
                    newState: activeCanvas.toJSON(),
                  })
                }}
              />
            </div>
            <div className="font-controls">
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {isActiveTextObject && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginRight: '20px',
                    }}
                  >
                    <label>{t('font_style')} &nbsp;</label>
                    <Form.Control
                      title={t('font_style')}
                      as="select"
                      value={fontFamilyValue}
                      onChange={textFontFamily}
                    >
                      <option value="" selected>
                        {t('font_style')}
                      </option>
                      <option
                        style={{ fontFamily: 'Myriad Pro Regular' }}
                        value="helvetica"
                        selected
                      >
                        Helvetica
                      </option>
                      {fonts.map((fontFamily) => (
                        <option
                          key={fontFamily}
                          style={{ fontFamily }}
                          value={fontFamily}
                        >
                          {fontFamily}
                        </option>
                      ))}
                    </Form.Control>
                  </div>
                )}
                {activeObject && (
                  <div className="d-flex flex-column">
                    <div className="color-tool">
                      <ColorPicker
                        className="strokeColor"
                        onChange={selectStrokeColor}
                        onClose={() =>
                          dispatchHistory({
                            type: 'PUSH',
                            activeCanvas: currentEditTarget,
                            newState: activeCanvas.toJSON(),
                          })
                        }
                        color={strokeColorValue}
                      />
                      <label>{t('stroke_color')} </label>
                    </div>

                    <div className="color-tool">
                      <ColorPicker
                        className="fillColor"
                        onChange={selectFillColor}
                        onClose={() =>
                          dispatchHistory({
                            type: 'PUSH',
                            activeCanvas: currentEditTarget,
                            newState: activeCanvas.toJSON(),
                          })
                        }
                        color={fillColorValue}
                      />
                      <label>
                        {t(isActiveTextObject ? 'font_color' : 'fill_color')}{' '}
                      </label>
                    </div>

                    {!isActiveImgObject && (
                      <div>
                        <label>Stroke Width</label>
                        <input
                          type="number"
                          value={strokeWidth}
                          onChange={handleStrokeWidthChange}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </BookCoverContainer>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <ToastContainer />
    </>
  )
}

export default withRouter(CoverEditor)
